import nightOwl from '@theme-ui/prism/presets/night-owl.json';

export default {
  initialColorModeName: 'light',
  colors: {
    text: '#111',
    background: '#fff',
    primary: '#111',
    secondary: '#111',
    accent: '#111',
    muted: '#e0e0e0',
    gray: '#333',
    highlight: '#033454',
    modes: {
      black: {
        text: '#f8f8f8',
        background: '#0a0a0a',
        primary: '#00e5ff',
        secondary: 'hsl(173, 40%, 55%)',
        accent: '#e600e6',
        muted: 'hsla(244, 28%, 25%, 45%)',
        gray: '#999',
      },
      dark: {
        text: '#f5f5f5',
        background: 'hsl(180, 6%, 16%)',
        primary: 'hsl(50, 98%, 60%)',
        secondary: 'hsl(52, 58%, 43%)',
        accent: 'hsl(310, 98%, 60%)',
        muted: 'hsla(180, 6%, 8%, 45%)',
        gray: 'hsl(180, 0%, 75%)',
      },
      deep: {
        text: '#f8f8f8',
        background: 'hsl(230, 28%, 19%)',
        primary: 'hsl(260, 98%, 82%)',
        secondary: 'hsl(21, 80%, 84%)',
        accent: 'hsl(290, 98%, 82%)',
        muted: 'hsla(230, 22%, 5%, 45%)',
        gray: 'hsl(210, 52%, 65%)',
      },
      hack: {
        text: '#68d391',
        background: '#171923',
        primary: '#48bb78',
        secondary: '#38b2ac',
        accent: '#9ae6b4',
        muted: 'rgba(45, 55, 72, 0.5)',
        gray: '#4a5568',
      },
      pink: {
        text: '#4a1a35',
        background: '#fff5f7',
        primary: '#b91c1c',
        secondary: '#ed64a6',
        accent: '#d97979',
        muted: 'rgba(237, 100, 166, 0.2)',
        gray: '#97266d',
      },
    },
  },
  fonts: {
    body: '"General Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    monospace: '"JetBrains Mono", "Fira Code", Menlo, monospace',
  },
  lineHeights: {
    body: 1.6,
    heading: 1.25,
  },
  fontWeights: {
    body: 400,
    heading: 600,
    bold: 700,
  },
  fontSizes: [13, 15, 16, 18, 20, 24, 32, 48],
  sizes: {
    container: 960,
    wide: 960,
  },
  space: [0, 4, 8, 16, 24, 32, 48, 64],
  text: {
    heading: {
      fontSize: 5,
      fontWeight: 'heading',
      lineHeight: 'heading',
      my: 4,
    },
    small: {
      fontSize: 0,
    },
    caps: {
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
  },
  styles: {
    root: {
      fontFamily: 'body',
      lineHeight: 'body',
      fontSize: 2,
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      transitionProperty: 'background-color, color',
      transitionTimingFunction: 'ease-out',
      transitionDuration: '.4s',
    },
    h1: {
      fontSize: 5,
      fontWeight: 900,
      lineHeight: 'heading',
      my: 4,
    },
    h2: {
      fontSize: 4,
      fontWeight: 600,
      lineHeight: 'heading',
      mt: 5,
      mb: 3,
    },
    h3: {
      fontSize: 3,
      fontWeight: 600,
    },
    h4: {
      fontSize: 2,
      fontWeight: 600,
    },
    h5: {
      fontSize: 2,
      fontWeight: 600,
    },
    h6: {
      fontSize: 1,
      fontWeight: 600,
    },
    p: {
      lineHeight: 1.6,
      mb: 3,
      code: {
        color: 'text',
      },
    },
    a: {
      color: 'text',
      textDecoration: 'underline',
      textDecorationSkipInk: 'auto',
      ':hover': {
        cursor: 'pointer',
      },
    },
    img: {
      maxWidth: '100%',
      height: 'auto',
    },
    hr: {
      border: 0,
      my: 5,
      borderBottom: '1px solid',
      borderColor: 'muted',
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    th: {
      textAlign: 'left',
      py: 2,
      borderBottomStyle: 'solid',
    },
    td: {
      textAlign: 'left',
      py: 2,
      borderBottom: '1px solid',
      borderColor: 'muted',
    },
    blockquote: {
      fontWeight: 'bold',
      mx: 0,
      px: 3,
      my: 5,
      borderLeft: '4px solid',
      borderColor: 'muted',
    },
    div: {
      '&.footnotes': {
        variant: 'text.small',
      },
    },
    navlink: {
      color: 'inherit',
      textDecoration: 'none',
      fontWeight: 500,
      letterSpacing: '-0.015em',
      ':hover,:focus': {
        textDecoration: 'underline',
      },
    },
    navitem: {
      variant: 'styles.navlink',
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: [1, 2],
    },
    shortcut: {
      variant: 'styles.navlink',
      fontSize: 1,
    },
    pre: {
      ...nightOwl,
      boxSizing: 'border-box',
      fontFamily: 'monospace',
      whiteSpace: 'pre',
      wordWrap: 'break-word',
      wordSpacing: 'normal',
      wordBreak: 'normal',
      overflow: 'auto',
      py: 3,
      px: 3,
      fontFeatureSettings: 'normal',
      textAlign: 'left',
      overflowWrap: 'normal',
      lineHeight: '1.5',
      marginBottom: '0px',
      tabSize: 4,
      hyphens: 'none',
      borderRadius: '2px',
      '.highlight': {
        backgroundColor: 'highlight',
        borderWidth: '10px',
        borderLeft: 'solid',
        borderColor: 'primary',
        mr: '-10rem',
        ml: -3,
      },
    },
    code: {
      fontFamily: 'monospace',
      px: 1,
      color: 'text',
    },
    inlineCode: {
      fontFamily: 'monospace',
      color: 'text',
    },
  },
};
